import { useCallback, useMemo } from 'react'
import { toast } from 'sonner'

interface ShareData {
  url: string
  title: string
  description?: string
}

interface UseShareReturn {
  share: () => Promise<void>
  copyLink: () => Promise<void>
  canNativeShare: boolean
  shareOnX: () => void
  shareOnFacebook: () => void
  shareViaEmail: () => void
}

export function useShare(data: ShareData): UseShareReturn {
  const canNativeShare = useMemo(
    () => typeof navigator !== 'undefined' && !!navigator.share,
    []
  )

  const copyLink = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(data.url)
      toast.success('Link copied!')
    } catch {
      toast.error('Failed to copy link')
    }
  }, [data.url])

  const share = useCallback(async () => {
    if (canNativeShare) {
      try {
        await navigator.share({
          title: data.title,
          text: data.description,
          url: data.url,
        })
      } catch (err) {
        // User cancelled share — not an error
        if (err instanceof Error && err.name === 'AbortError') return
        // Fallback to copy
        await copyLink()
      }
    } else {
      await copyLink()
    }
  }, [canNativeShare, data, copyLink])

  const shareOnX = useCallback(() => {
    const text = encodeURIComponent(data.title)
    const url = encodeURIComponent(data.url)
    window.open(
      `https://x.com/intent/tweet?text=${text}&url=${url}`,
      '_blank',
      'noopener,noreferrer'
    )
  }, [data.title, data.url])

  const shareOnFacebook = useCallback(() => {
    const url = encodeURIComponent(data.url)
    window.open(
      `https://www.facebook.com/sharer/sharer.php?u=${url}`,
      '_blank',
      'noopener,noreferrer'
    )
  }, [data.url])

  const shareViaEmail = useCallback(() => {
    const subject = encodeURIComponent(data.title)
    const body = encodeURIComponent(
      `${data.description ? data.description + '\n\n' : ''}${data.url}`
    )
    window.location.href = `mailto:?subject=${subject}&body=${body}`
  }, [data.title, data.description, data.url])

  return { share, copyLink, canNativeShare, shareOnX, shareOnFacebook, shareViaEmail }
}
